import React, {useMemo, useState} from "react";
import {Error as ErrorIcon, MoreVert, Settings} from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  DialogActions,
  DialogContent,
  Grid,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListSubheader,
  MenuItem,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import {useTranslation} from "react-i18next";
import {useSelector} from "react-redux";
import ButtonSelect from "../button-select";
import CustomDialog from "../custom-dialog";
import CustomDialogTitle from "../custom-dialog-title";
import {
  COLUMN_TYPES,
  getColumnTypes,
  getCoordinatesTypes,
  getDecimalSeparators,
  getGenericTypes,
  getMeasureDecimals,
  getMeasureUnit,
  getTemporalTypes,
  INITIAL_COLUMN_TYPE
} from "./constants";
import {etlSelector} from "../../state/package-manager/packageManagerSelectors";
import {isCoordinates, isMeasure, isTimeColumnWithFormat} from "./utils";

const ColumnTypeRow = ({column, onChange}) => {
  const {t} = useTranslation();
  const {codelist, dateFormats} = useSelector(etlSelector);

  const selectedValue = useMemo(() => {
    switch (column.dimensionType) {
      case COLUMN_TYPES.generic:
        return `${COLUMN_TYPES.generic}-${column.genericDimensionType}`;
      case COLUMN_TYPES.measure:
        return `${COLUMN_TYPES.measure}-${column.measureUnit}`;
      case COLUMN_TYPES.coded:
        return `${COLUMN_TYPES.coded}-${column.codelistIdentifier}`;
      default:
        return "";
    }
  }, [column]);

  const hasSettings = column => isMeasure(column) || isTimeColumnWithFormat(column) || isCoordinates(column);
  const hasMissingSettings = column => !column.formatDate || !column.formatCoordinates || !column.decimalSeparator;

  const [open, setOpen] = useState(false);
  const handleClose = () => setOpen(false);

  return (
    codelist !== null && (
      <ListItem className="etl-wizard__list__body__item">
        <Grid container className="etl-wizard__list__body__row">
          <Grid size={3} className="etl-wizard__column-type__item__title">
            <Tooltip title={column.alias}>
              <Typography>{column.alias}</Typography>
            </Tooltip>
          </Grid>
          <Grid
            size={8}
            className="etl-wizard__column-type__item__fields__dimension-type"
            sx={isMeasure(column) && {border: 0}}
            columnGap={2}
          >
            <Grid size={"grow"}>
              <TextField
                select
                fullWidth
                className="etl-wizard_column-type__item__fields__dimension-type__field"
                value={selectedValue}
                variant="outlined"
                slotProps={{
                  input: {
                    endAdornment: hasSettings(column) && (
                      <InputAdornment
                        className="etl-wizard_column-type__item__fields__dimension-type__settings"
                        sx={{marginRight: "12px"}}
                      >
                        <Tooltip
                          placement="top"
                          title={
                            <Box className="etl-wizard_column-type__item__fields__dimension-type__settings-tooltip">
                              <Typography variant="p" sx={{display: "block"}}>
                                <b>{t("modules.packageManager.etlWizard.columnType.dialog.tooltip.title")}</b>
                              </Typography>
                              {column?.hasError && hasMissingSettings(column) && (
                                <Typography variant="p">
                                  {t("modules.packageManager.etlWizard.columnType.dialog.tooltip.error")}
                                </Typography>
                              )}
                              {isMeasure(column) && column?.decimalSeparator && (
                                <Typography variant="p">
                                  {t("modules.packageManager.etlWizard.columnType.dialog.tooltip.decimalSeparator") +
                                    " " +
                                    getDecimalSeparators(t).find(separator => separator.id === column.decimalSeparator)
                                      ?.label}
                                </Typography>
                              )}
                              {isTimeColumnWithFormat(column) && column?.formatDate && (
                                <Typography variant="p">
                                  {t("modules.packageManager.etlWizard.columnType.dialog.tooltip.dateFormat") +
                                    " " +
                                    column.formatDate}
                                </Typography>
                              )}
                              {isCoordinates(column) && column?.formatCoordinates && (
                                <Typography variant="p">
                                  {t("modules.packageManager.etlWizard.columnType.dialog.tooltip.coordinatesFormat") +
                                    " " +
                                    column.formatCoordinates}
                                </Typography>
                              )}
                            </Box>
                          }
                        >
                          <IconButton onClick={() => setOpen(true)}>
                            {column?.hasError && hasMissingSettings(column) ? (
                              <ErrorIcon color="error" />
                            ) : (
                              <Settings />
                            )}
                          </IconButton>
                        </Tooltip>
                        <CustomDialog className="etl-wizard__column-type-settings" open={open} onClose={handleClose}>
                          <CustomDialogTitle onClose={handleClose}>
                            {t("modules.packageManager.etlWizard.columnType.dialog.title")}
                          </CustomDialogTitle>
                          <DialogContent>
                            {isMeasure(column) && (
                              <Grid container sx={{minWidth: "200px", padding: "16px 0"}}>
                                <Grid size={12} display={"flex"} alignItems={"center"}>
                                  <Typography whiteSpace={"nowrap"} sx={{marginRight: "16px"}}>
                                    {t("modules.packageManager.etlWizard.columnType.fields.decimalSeparator.title")}
                                  </Typography>
                                  <TextField
                                    select
                                    fullWidth
                                    className="etl-wizard_column-type__item__fields__measure-digits__field"
                                    label={t(
                                      "modules.packageManager.etlWizard.columnType.fields.decimalSeparator.label"
                                    )}
                                    value={column.decimalSeparator ?? ""}
                                    onChange={e => onChange({...column, decimalSeparator: e.target.value})}
                                    variant="outlined"
                                    required
                                    error={column?.hasError && !column.decimalSeparator}
                                  >
                                    {getDecimalSeparators(t).map(separator => (
                                      <MenuItem key={separator.id} value={separator.id}>
                                        {separator.label}
                                      </MenuItem>
                                    ))}
                                  </TextField>
                                </Grid>
                              </Grid>
                            )}
                            {isTimeColumnWithFormat(column) && (
                              <Grid container sx={{minWidth: "200px", padding: "16px 0"}}>
                                <Grid size={12} display={"flex"} alignItems={"center"}>
                                  <Typography whiteSpace={"nowrap"} sx={{marginRight: "16px"}}>
                                    {t("modules.packageManager.etlWizard.columnType.fields.dateFormat.title")}
                                  </Typography>
                                  <Autocomplete
                                    freeSolo
                                    fullWidth
                                    autoSelect
                                    className="etl-wizard__column-type__item__fields__date-format__field"
                                    options={dateFormats[column.genericDimensionType] ?? []}
                                    value={column?.formatDate ?? ""}
                                    onChange={(_e, value) => onChange({...column, formatDate: value})}
                                    renderInput={params => (
                                      <TextField
                                        {...params}
                                        required
                                        label={t("modules.packageManager.etlWizard.columnType.fields.dateFormat.label")}
                                        error={column?.hasError && !column.formatDate}
                                        variant="outlined"
                                      />
                                    )}
                                  />
                                </Grid>
                              </Grid>
                            )}
                            {isCoordinates(column) && (
                              <Grid container sx={{minWidth: "200px", padding: "16px 0"}}>
                                <Grid size={12} display={"flex"} alignItems={"center"}>
                                  <Typography whiteSpace={"nowrap"} sx={{marginRight: "16px"}}>
                                    {t("modules.packageManager.etlWizard.columnType.fields.coordinatesFormat.title")}
                                  </Typography>
                                  <TextField
                                    select
                                    fullWidth
                                    className="etl-wizard__column-type__item__fields__coordinates-format__field"
                                    label={t(
                                      "modules.packageManager.etlWizard.columnType.fields.coordinatesFormat.label"
                                    )}
                                    value={column.formatCoordinates ?? ""}
                                    onChange={e => onChange({...column, formatCoordinates: e.target.value})}
                                    variant="outlined"
                                    required
                                    error={column?.hasError && !column.formatCoordinates}
                                  >
                                    {getDecimalSeparators(t).map(separator => (
                                      <MenuItem key={separator.id} value={separator.id}>
                                        {separator.label}
                                      </MenuItem>
                                    ))}
                                  </TextField>
                                </Grid>
                              </Grid>
                            )}
                          </DialogContent>
                          <DialogActions>
                            <Button onClick={handleClose}>{t("commons.confirm.close")}</Button>
                          </DialogActions>
                        </CustomDialog>
                      </InputAdornment>
                    )
                  }
                }}
              >
                {getGenericTypes(t).map(type => (
                  <MenuItem
                    key={type.id}
                    value={`${COLUMN_TYPES.generic}-${type.id}`}
                    onClick={() =>
                      onChange({
                        ...column,
                        dimensionType: COLUMN_TYPES.generic,
                        genericDimensionType: type.id
                      })
                    }
                  >
                    {type.label}
                  </MenuItem>
                ))}
                <ListSubheader>{getColumnTypes(t).temporal}</ListSubheader>
                {getTemporalTypes(t).map(type => (
                  <MenuItem
                    key={type.id}
                    value={`${COLUMN_TYPES.generic}-${type.id}`}
                    onClick={() =>
                      onChange({
                        ...column,
                        dimensionType: COLUMN_TYPES.generic,
                        genericDimensionType: type.id
                      })
                    }
                  >
                    {type.label}
                  </MenuItem>
                ))}
                <ListSubheader>{getColumnTypes(t).coordinates}</ListSubheader>
                {getCoordinatesTypes(t).map(type => (
                  <MenuItem
                    key={type.id}
                    value={`${COLUMN_TYPES.generic}-${type.id}`}
                    onClick={() =>
                      onChange({
                        ...column,
                        dimensionType: COLUMN_TYPES.generic,
                        genericDimensionType: type.id
                      })
                    }
                  >
                    {type.label}
                  </MenuItem>
                ))}
                <ListSubheader>{getColumnTypes(t)[COLUMN_TYPES.measure]}</ListSubheader>
                {getMeasureUnit(t).map(unit => (
                  <MenuItem
                    key={unit.id}
                    value={`${COLUMN_TYPES.measure}-${unit.id}`}
                    onClick={() =>
                      onChange({
                        ...column,
                        dimensionType: COLUMN_TYPES.measure,
                        measureUnit: unit.id
                      })
                    }
                  >
                    {unit.label}
                  </MenuItem>
                ))}
                {(codelist || []).length > 0 && <ListSubheader>{getColumnTypes(t)[COLUMN_TYPES.coded]}</ListSubheader>}
                {(codelist || []).map(code => (
                  <MenuItem
                    key={code.DataStoreId}
                    value={`${COLUMN_TYPES.coded}-${code.DataStoreId}`}
                    onClick={() =>
                      onChange({
                        ...column,
                        dimensionType: COLUMN_TYPES.coded,
                        codelistIdentifier: code.DataStoreId
                      })
                    }
                  >
                    {code.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            {isMeasure(column) && (
              <>
                <Grid size={3} className="etl-wizard__column-type__item__fields__measure-digits">
                  <TextField
                    select
                    fullWidth
                    className="etl-wizard_column-type__item__fields__measure-digits__field"
                    label={t("modules.packageManager.etlWizard.columnType.fields.measureDigits.label")}
                    value={column.measureDigits ?? ""}
                    onChange={e => onChange({...column, measureDigits: e.target.value})}
                    variant="outlined"
                  >
                    <MenuItem value="">
                      <em>{t("modules.packageManager.etlWizard.columnType.fields.measureDigits.values.none")}</em>
                    </MenuItem>
                    {getMeasureDecimals(t).map(decimal => (
                      <MenuItem key={decimal.id} value={decimal.id}>
                        {decimal.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid size={3} className="etl-wizard__column-type__item__fields__measure-aggregable">
                  <TextField
                    select
                    fullWidth
                    className="etl-wizard_column-type__item__fields__measure-aggregable__field"
                    label={t("modules.packageManager.etlWizard.columnType.fields.aggregability.label")}
                    value={column.aggregable ?? 0}
                    onChange={e => onChange({...column, aggregable: e.target.value})}
                    variant="outlined"
                  >
                    <MenuItem value={true}>
                      {t("modules.packageManager.etlWizard.columnType.fields.aggregability.values.aggregable")}
                    </MenuItem>
                    <MenuItem value={false}>
                      {t("modules.packageManager.etlWizard.columnType.fields.aggregability.values.notAggregable")}
                    </MenuItem>
                  </TextField>
                </Grid>
              </>
            )}
          </Grid>
          <Grid size={1} className="etl-wizard__column-type__item__fields__checkboxes">
            <Checkbox checked={!column.visible} onChange={() => onChange({...column, visible: !column.visible})} />
          </Grid>
        </Grid>
      </ListItem>
    )
  );
};

const EtlWizardStepType = ({columns, setColumns, validate, errorMessage}) => {
  const {t} = useTranslation();

  const {initialSuggestions} = useSelector(etlSelector);

  const handleColumnChange = updatedColumn => {
    setColumns(
      columns.map(column =>
        column.name === updatedColumn.name ? {...updatedColumn, hasError: validate(updatedColumn)} : column
      )
    );
  };

  const removeColumsSuggestion = () => {
    setColumns(columns.map(column => ({...column, ...INITIAL_COLUMN_TYPE})));
  };

  const resetColumsSuggestion = () => {
    setColumns(columns.map(column => ({...column, ...initialSuggestions[column.name], hasError: false})));
  };

  return (
    <Box
      className="etl-wizard__step-type"
      sx={
        errorMessage
          ? {
              marginTop: "16px",
              height: "calc(100% - 200px)"
            }
          : {
              marginTop: "24px",
              height: "calc(100% - 150px)"
            }
      }
    >
      <List className="etl-wizard__list__header">
        <ListItem className="etl-wizard__list__header__item">
          <Grid container className="etl-wizard__list__header__row">
            <Grid size={3}>
              <Typography variant="p">
                {t("modules.packageManager.etlWizard.columnType.headers.column.label")}
              </Typography>
            </Grid>
            <Grid size={8} sx={{justifyContent: "space-between"}}>
              <Typography variant="p">{t("modules.packageManager.etlWizard.columnType.headers.type.label")}</Typography>
              <ButtonSelect
                icon={<MoreVert fontSize="small" />}
                ariaLabel={t("modules.packageManager.etlWizard.columnType.headers.type.actions.label")}
                tooltip={t("modules.packageManager.etlWizard.columnType.headers.type.actions.label")}
              >
                <div onClick={() => removeColumsSuggestion()}>
                  {t("modules.packageManager.etlWizard.columnType.headers.type.actions.values.removeSuggestion")}
                </div>
                <div onClick={() => resetColumsSuggestion()}>
                  {t("modules.packageManager.etlWizard.columnType.headers.type.actions.values.resetSuggestion")}
                </div>
              </ButtonSelect>
            </Grid>
            <Grid size={1}>
              <Typography variant="p">
                {t("modules.packageManager.etlWizard.columnType.headers.hidden.label")}
              </Typography>
            </Grid>
          </Grid>
        </ListItem>
      </List>
      <List className="etl-wizard__list__body">
        {columns
          .filter(column => column.import)
          .map((column, idx) => (
            <ColumnTypeRow key={column.name} idx={idx} column={column} onChange={handleColumnChange} />
          ))}
      </List>
    </Box>
  );
};

export default EtlWizardStepType;
